import React, { useMemo, Fragment } from 'react';
import cl from 'classnames';
import { uuidV4 } from 'src/helper';
import { Select } from 'src/components/general';
import styles from './switcherStyle.module.scss';

type SwitcherProps = {
  currentId: string;
  list: {
    id: string;
    label: string;
    inactive?: boolean;
    isCb?: boolean;
    count?: number;
  }[];
  onChange: (value: string) => void;
  selectedCbIDs?: Set<string>;
  selectCbItem?: (id: string, value: boolean) => void;
  className?: string;
  selectClassName?: string;
  onlyDesktop?: boolean;
  onlyMobile?: boolean;
};

export const Switcher = ({
  currentId,
  list,
  onChange,
  className,
  selectedCbIDs,
  selectCbItem,
  selectClassName,
  onlyDesktop = false,
  onlyMobile = false,
}: SwitcherProps) => {
  const uuid = useMemo(uuidV4, []);
  const filteredList = useMemo(() => list.filter(({ isCb }) => !isCb), [list]);
  const activeIndex = useMemo(
    () => filteredList.findIndex(({ id }) => id === currentId),
    [filteredList, currentId],
  );

  return (
    <div className={cl(styles.container, className)}>
      {!onlyMobile && (
        <div className={styles.buttons}>
          {filteredList.map(({ id, label, count }, index) => (
            <Fragment key={`sw-bt-${id}-${uuid}`}>
              <div
                className={cl(
                  styles.button,
                  currentId === id && styles.current,
                )}
                onClick={() => {
                  onChange(id);
                }}
              >
                <div className={styles.label}>{label}</div>
                {count !== undefined && (
                  <div className={cl(styles.count, count === 0 && styles.zero)}>
                    {count}
                  </div>
                )}
              </div>
              {index !== filteredList.length - 1 && (
                <div
                  className={cl(
                    styles.separator,
                    (index === activeIndex || index === activeIndex - 1) &&
                      styles.hide,
                  )}
                />
              )}
            </Fragment>
          ))}
        </div>
      )}
      {!onlyDesktop && (
        <div className={styles.mobileGroup}>
          <Select
            value={currentId}
            items={list.map(({ id, label, isCb, count, inactive }) => ({
              value: id,
              label,
              isCb,
              count,
              inactive,
            }))}
            selectItem={(id) => {
              onChange(id as string);
            }}
            selectedCbIDs={selectedCbIDs}
            selectCbItem={selectCbItem}
            design="switcherBorder"
            className={selectClassName}
          />
        </div>
      )}
    </div>
  );
};

export default Switcher;
